import { Plus, X } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MAX_SUGGESTED_FAQ, type ProductTypeFormInput } from '../schemas/product-type-form-schema';

export interface ProductTypeFaqSectionProps {
  readOnly: boolean;
}

/**
 * Questions offered on every product of this type. The product editor lists them and staff copy
 * the ones that fit; nothing reaches a product on its own (D-160).
 */
export function ProductTypeFaqSection({ readOnly }: ProductTypeFaqSectionProps) {
  const { t } = useTranslation('productTypes');
  const form = useFormContext<ProductTypeFormInput>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'suggestedFaq',
  });
  const listError = form.formState.errors.suggestedFaq?.root ?? form.formState.errors.suggestedFaq;

  return (
    <FormSection title={t('sections.faq')} description={t('sections.faqDescription')}>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('faq.none')}</p>
      ) : (
        <ol className="flex flex-col gap-3" aria-label={t('faq.label')}>
          {fields.map((item, index) => (
            <li key={item.id} className="flex flex-col gap-2 rounded-md border p-3">
              <div className="flex items-start gap-2">
                <FormField
                  control={form.control}
                  name={`suggestedFaq.${index}.question`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('faq.question', { n: index + 1 })}</FormLabel>
                      <FormControl>
                        <Input disabled={readOnly} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {readOnly ? null : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground mt-6"
                    aria-label={t('faq.remove', { n: index + 1 })}
                    onClick={() => remove(index)}
                  >
                    <X aria-hidden />
                  </Button>
                )}
              </div>
              <FormField
                control={form.control}
                name={`suggestedFaq.${index}.answer`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('faq.answer', { n: index + 1 })}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} disabled={readOnly} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </li>
          ))}
        </ol>
      )}
      {typeof listError?.message === 'string' ? (
        <p className="text-destructive text-xs">{t(listError.message)}</p>
      ) : null}
      {readOnly ? null : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_SUGGESTED_FAQ}
            onClick={() => append({ question: '', answer: '' })}
          >
            <Plus aria-hidden />
            {t('faq.add')}
          </Button>
        </div>
      )}
    </FormSection>
  );
}
